import {
  ButtonWidget
} from '@tableau/widgets';
import * as React from 'react';

import ReactDOM = require('react-dom');
import { Formatters, Messages } from '../../../gen-src/Localize';
import { SiteUrlsProvider, SiteUrlsProviderProps } from '../DataCatalogProvider';
import { DataQualityDetailPanelSide } from '../dataQualityIndicators/DataQualityIndicatorSharedHelpers';
import { DataQualityDetailProps, DataQualityTooltipButton, DataQualityTooltipButtonBaseProps } from './DataQualityDetail';
import { DataQualityDetailPanel } from './DataQualityDetailPanel';
import { localizeDataQualityTypeName } from './DataQualityType';

const DataQualityStatusChipId = 'data-quality-status-chip-id';

export type DataQualityStatusChipProps = {
  readonly details: ReadonlyArray<DataQualityDetailProps>;
  readonly iconOnly: boolean;
  readonly linksDisabled?: boolean;
  readonly side?: DataQualityDetailPanelSide;
  readonly stackingRootId?: string;
  readonly iconOnlyTabIndex?: number;
};

export type DataQualityStatusChipState = {
  readonly iconOnlyButtonRef?: HTMLElement; // TODO: ask the shared widgets team to use forwardRef on ButtonWidget
  readonly isDialogOpen: boolean;
};

export function EmbeddableDataQualityStatusChip(props: DataQualityStatusChipProps & SiteUrlsProviderProps) {
  return (
    <SiteUrlsProvider siteUrls={props.siteUrls}>
      <DataQualityStatusChip {...props} />
    </SiteUrlsProvider >
  );
}

export class DataQualityStatusChip extends React.Component<DataQualityStatusChipProps, DataQualityStatusChipState> {
  public constructor(props: DataQualityStatusChipProps) {
    super(props);
    this.state = {
      isDialogOpen: false
    };
  }

  private readonly setIsDialogOpen = (isDialogOpen: boolean) => {
    this.setState({ isDialogOpen });
  }

  private ref: React.RefObject<HTMLDivElement> = React.createRef();

  private readonly setRef = (button: ButtonWidget) => {
    if (button) {
      // eslint-disable-next-line react/no-find-dom-node
      this.setState({ iconOnlyButtonRef: ReactDOM.findDOMNode(button) as HTMLElement });
    }
  }

  public render(): JSX.Element | null {
    if (!this.props || this.props.details.length < 1) {
      return null;
    }

    const isSevere = this.props.details.some(d => d.isSevere);
    const text = dataQualityTooltipTitle(this.props);
    const baseProps: DataQualityTooltipButtonBaseProps = { text, warningKind: isSevere ? 'severe-warning' : 'standard-warning', testId: DataQualityStatusChipId };

    return (
      <>
        {
          this.props.iconOnly ? (
            <DataQualityTooltipButton
              {...baseProps}
              kind={'interactive-icon'}
              setRef={this.setRef}
              onClick={() => this.setIsDialogOpen(!this.state.isDialogOpen)}
              tabIndex={this.props.iconOnlyTabIndex}
            />
          ) : (
            <DataQualityTooltipButton
              {...baseProps}
              kind={'interactive-inline'}
              ref={this.ref}
              onClick={() => this.setIsDialogOpen(!this.state.isDialogOpen)}
            />
          )
        }
        {
          this.state.isDialogOpen ? (
            <DataQualityDetailPanel
              {...this.props}
              title={text}
              anchorEl={
                this.props.iconOnly
                  ? (this.state.iconOnlyButtonRef as HTMLElement || undefined)
                  : this.ref.current as HTMLElement
              }
              side={this.props.side || 'right'}
              onCloseRequest={() => this.setState({ isDialogOpen: false })}
            />
          ) : null
        }
      </>
    );
  }
}

function dataQualityTooltipTitle(props: DataQualityStatusChipProps): string {
  if (!props || props.details.length < 1) {
    return '';
  }

  const count = props.details.length;

  if (count === 1) {
    return localizeDataQualityTypeName(props.details[0].type);
  }

  return Messages.DataQualityIconMessage({
    MESSAGE_COUNT: count,
    MESSAGE_COUNT_LOC: Formatters.formatCount(count)
  });
}
